import React, { useEffect, useState } from 'react'
import {
  Tabs,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import blogService from '../services/blogService'

import { adminPath } from '../../shared/config/paths';

const locales = [
  { code: 'en', label: 'English' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'ms', label: 'Bahasa Melayu' }
]

const BlogManager = () => {
  const [activeTab, setActiveTab] = useState('posts')

  return (
    <Card title="博客与内容管理">
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="文章管理" key="posts">
          <BlogPostManager />
        </Tabs.TabPane>
        <Tabs.TabPane tab="分类" key="categories">
          <BlogCategoryManager />
        </Tabs.TabPane>
        <Tabs.TabPane tab="标签" key="tags">
          <BlogTagManager />
        </Tabs.TabPane>
      </Tabs>
    </Card>
  )
}

const BlogPostManager = () => {
  const navigate = useNavigate()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(false)

  const loadPosts = async () => {
    setLoading(true)
    try {
      const response = await blogService.getPosts()
      if (response.success !== false) {
        const items = response?.data?.items || response?.items || []
        setPosts(Array.isArray(items) ? items : [])
      } else {
        message.error(response.error || '加载文章失败')
      }
    } catch (error) {
      message.error(error?.message || '加载文章失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPosts()
  }, [])

  const handleCreate = () => {
    navigate(adminPath('/blog/new'))
  }

  const handleEdit = (record) => {
    navigate(adminPath(`/blog/${record.id}/edit`))
  }

  const handleDelete = async (record) => {
    Modal.confirm({
      title: '确认删除该文章？',
      content: '删除后不可恢复',
      okText: '删除',
      okType: 'danger',
      onOk: async () => {
        try {
          await blogService.deletePost(record.id)
          message.success('文章已删除')
          loadPosts()
        } catch (error) {
          message.error('删除失败')
        }
      }
    })
  }

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      render: (_, record) => record.title || record.slug
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => (
        <Tag color={value === 'published' ? 'green' : value === 'draft' ? 'default' : 'blue'}>{value}</Tag>
      )
    },
    {
      title: '分类',
      dataIndex: 'categories',
      render: (_, record) =>
        record.categories?.map((cat) => (
          <Tag key={cat.id} color="blue">
            {cat.name}
          </Tag>
        )) || '—'
    },
    {
      title: '发布时间',
      dataIndex: 'published_at',
      render: (value) => (value ? new Date(value).toLocaleString() : '—')
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      )
    }
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建文章
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadPosts}>
          刷新
        </Button>
      </Space>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={posts} />
    </>
  )
}

const BlogCategoryManager = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [form] = Form.useForm()

  const loadCategories = async () => {
    setLoading(true)
    try {
      const response = await blogService.getCategories()
      setCategories(response.data || response || [])
    } catch (error) {
      message.error('加载分类失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCategories()
  }, [])

  const openModal = (record) => {
    setEditingCategory(record || null)
    if (record) {
      form.setFieldsValue({
        ...record,
        translations: record.translations || {}
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        status: 'active',
        sort_order: 0,
        translations: locales.reduce((map, locale) => {
          map[locale.code] = { name: '', description: '' }
          return map
        }, {})
      })
    }
    setModalVisible(true)
  }

  const submitCategory = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        ...values,
        id: editingCategory?.id
      }

      if (editingCategory) {
        await blogService.updateCategory(payload)
        message.success('分类已更新')
      } else {
        await blogService.createCategory(payload)
        message.success('分类已创建')
      }

      setModalVisible(false)
      loadCategories()
    } catch (error) {
      if (error?.errorFields) return
      message.error('保存失败')
    }
  }

  const deleteCategory = (record) => {
    Modal.confirm({
      title: `删除分类 ${record.slug}?`,
      onOk: async () => {
        try {
          await blogService.deleteCategory(record.id)
          message.success('分类已删除')
          loadCategories()
        } catch (error) {
          message.error(error?.response?.data?.error || '删除失败')
        }
      }
    })
  }

  const columns = [
    { title: 'Slug', dataIndex: 'slug' },
    {
      title: '名称 (简体)',
      render: (_, record) => record.translations?.['zh-CN']?.name || '—'
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => <Tag color={value === 'active' ? 'green' : 'default'}>{value}</Tag>
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openModal(record)} icon={<EditOutlined />}>
            编辑
          </Button>
          <Button size="small" danger onClick={() => deleteCategory(record)} icon={<DeleteOutlined />}>
            删除
          </Button>
        </Space>
      )
    }
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>
          新建分类
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadCategories}>
          刷新
        </Button>
      </Space>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={categories} />

      <Modal
        open={modalVisible}
        title={editingCategory ? '编辑分类' : '新建分类'}
        onOk={submitCategory}
        onCancel={() => setModalVisible(false)}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Slug" name="slug" rules={[{ required: true, message: '请输入 slug' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="排序" name="sort_order" initialValue={0}>
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item label="状态" name="status" initialValue="active">
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'archived', label: '停用' }
              ]}
            />
          </Form.Item>
          <Form.Item label="图标 (emoji)" name="icon">
            <Input placeholder="☁" />
          </Form.Item>
          <Form.Item label="强调颜色" name="accent_color">
            <Input placeholder="#0b5cd7" />
          </Form.Item>

          <Tabs style={{ marginTop: 16 }}>
            {locales.map((locale) => (
              <Tabs.TabPane tab={locale.label} key={locale.code}>
                <Form.Item
                  label="名称"
                  name={['translations', locale.code, 'name']}
                  rules={[{ required: locale.code === 'zh-CN', message: '请输入名称' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label="描述" name={['translations', locale.code, 'description']}>
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Tabs.TabPane>
            ))}
          </Tabs>
        </Form>
      </Modal>
    </>
  )
}

const BlogTagManager = () => {
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingTag, setEditingTag] = useState(null)
  const [form] = Form.useForm()

  const loadTags = async () => {
    setLoading(true)
    try {
      const response = await blogService.getTags()
      setTags(response.data || response || [])
    } catch (error) {
      message.error('加载标签失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTags()
  }, [])

  const openModal = (record) => {
    setEditingTag(record || null)
    if (record) {
      form.setFieldsValue({
        ...record,
        translations: record.translations || {}
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        status: 'active',
        translations: locales.reduce((map, locale) => {
          map[locale.code] = { name: '' }
          return map
        }, {})
      })
    }
    setModalVisible(true)
  }

  const submitTag = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        ...values,
        id: editingTag?.id
      }
      if (editingTag) {
        await blogService.updateTag(payload)
        message.success('标签已更新')
      } else {
        await blogService.createTag(payload)
        message.success('标签已创建')
      }
      setModalVisible(false)
      loadTags()
    } catch (error) {
      if (error?.errorFields) return
      message.error('保存失败')
    }
  }

  const deleteTag = (record) => {
    Modal.confirm({
      title: `删除标签 ${record.slug}?`,
      onOk: async () => {
        try {
          await blogService.deleteTag(record.id)
          message.success('标签已删除')
          loadTags()
        } catch (error) {
          message.error(error?.response?.data?.error || '删除失败')
        }
      }
    })
  }

  const columns = [
    { title: 'Slug', dataIndex: 'slug' },
    {
      title: '名称 (简体)',
      render: (_, record) => record.translations?.['zh-CN']?.name || '—'
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => <Tag color={value === 'active' ? 'green' : 'default'}>{value}</Tag>
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openModal(record)} icon={<EditOutlined />}>
            编辑
          </Button>
          <Button size="small" danger onClick={() => deleteTag(record)} icon={<DeleteOutlined />}>
            删除
          </Button>
        </Space>
      )
    }
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>
          新建标签
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadTags}>
          刷新
        </Button>
      </Space>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={tags} />

      <Modal
        open={modalVisible}
        title={editingTag ? '编辑标签' : '新建标签'}
        onOk={submitTag}
        onCancel={() => setModalVisible(false)}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Slug" name="slug" rules={[{ required: true, message: '请输入 slug' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="状态" name="status" initialValue="active">
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'archived', label: '停用' }
              ]}
            />
          </Form.Item>
          <Form.Item label="颜色" name="color">
            <Input placeholder="#0b5cd7" />
          </Form.Item>

          <Tabs style={{ marginTop: 16 }}>
            {locales.map((locale) => (
              <Tabs.TabPane tab={locale.label} key={locale.code}>
                <Form.Item
                  label="名称"
                  name={['translations', locale.code, 'name']}
                  rules={[{ required: locale.code === 'zh-CN', message: '请输入名称' }]}
                >
                  <Input />
                </Form.Item>
              </Tabs.TabPane>
            ))}
          </Tabs>
        </Form>
      </Modal>
    </>
  )
}

export default BlogManager
